/**
 * SelectedExperience — accessible editorial accordion of numbered evidence rows.
 * First row starts expanded; every summary stays keyboard-accessible.
 * Explicitly attributed to the professional experience of the team,
 * including roles held before OceanNova was founded. No photography is used here,
 * so no stock image can be mistaken for documentary evidence.
 */

import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import Reveal from "@/components/common/Reveal";

interface Card {
  title: string;
  description: string;
}

const SelectedExperience = () => {
  const { t } = useTranslation();
  const cards = t("home.experience.cards", { returnObjects: true }) as Card[];
  const [open, setOpen] = useState<number>(0);

  return (
    <section
      id="experience"
      className="bg-muted/60 py-20 md:py-28 scroll-mt-20"
      aria-labelledby="experience-heading"
    >
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end mb-10 md:mb-14">
          <Reveal className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-5">
              <span className="rule w-10 shrink-0" aria-hidden="true" />
              <p className="eyebrow text-cobalt">{t("home.experience.label")}</p>
            </div>
            <h2 id="experience-heading" className="display-lg text-foreground">
              {t("home.experience.heading")}
            </h2>
          </Reveal>
          <Reveal delay={90} className="lg:col-span-5">
            <p className="text-sm md:text-base leading-relaxed text-muted-foreground lg:border-l lg:border-border lg:pl-6">
              {t("home.experience.note")}
            </p>
          </Reveal>
        </div>

        <div className="border-t border-foreground/20">
          {cards.map((card, index) => {
            const expanded = open === index;
            return (
              <Reveal
                key={card.title}
                delay={index * 60}
                className="border-b border-foreground/20"
              >
                <h3>
                  <button
                    type="button"
                    onClick={() => setOpen(expanded ? -1 : index)}
                    aria-expanded={expanded}
                    aria-controls={`experience-panel-${index}`}
                    className="group grid w-full grid-cols-[auto_1fr_auto] items-center gap-4 md:gap-8 py-5 md:py-7 text-left transition-colors duration-200 hover:bg-background/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cobalt"
                  >
                    <span className="index-num text-sm md:text-base text-cobalt w-8 md:w-12 pl-1">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="text-lg md:text-2xl font-bold tracking-tight text-foreground leading-snug group-hover:text-cobalt transition-colors duration-200">
                      {card.title}
                    </span>
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center border border-foreground/25 text-foreground mr-1"
                      aria-hidden="true"
                    >
                      <span
                        className="plus-stroke relative block h-4 w-4"
                        data-open={expanded}
                      >
                        <span className="absolute left-0 top-1/2 h-px w-4 -translate-y-1/2 bg-current" />
                        <span
                          className="plus-vertical absolute left-1/2 top-0 h-4 w-px -translate-x-1/2 bg-current"
                          data-open={expanded}
                        />
                      </span>
                    </span>
                  </button>
                </h3>
                <div
                  id={`experience-panel-${index}`}
                  className="accordion-grid"
                  data-open={expanded}
                  aria-hidden={!expanded}
                  {...(!expanded ? ({ inert: "" } as Record<string, string>) : {})}
                >
                  <div className="accordion-inner">
                    <div className="pb-6 md:pb-8 md:pl-20 pr-2">
                      <p className="max-w-3xl text-base md:text-lg leading-relaxed text-muted-foreground">
                        {card.description}
                      </p>
                    </div>
                  </div>
                </div>

              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default memo(SelectedExperience);
